import _, {Dictionary, groupBy} from 'lodash';

import {isTeamNamespace} from '../../../../helpers/models/namespaceHelpers';
import {computeParentTagId, rootLevelParentId} from '../../../../helpers/visualCustomViewHelpers';
import {TagModel} from '../../../../models/api/tagModel';
import {WorkspaceTagV2} from '../../../../models/api/workspaceModel';
import {
  computeVisualWorkspaceItemIsRecentlyViewed,
  computeWorkspaceTimestampMap,
  WorkspaceTimestampMap,
} from '../visualWorkspace/visualWorkspaceHelpers';
import {computeWorkspaceSortOrderMap, WorkspaceSortOrderMap} from './workspaceSortOrderMapHelpers';

/** A simplified version of {@see VisualCustomView} that only contains the details necessary to correctly sort tags/custom views. */
interface TagSortItem {
  tag: TagModel;
  nestingLevel: number;
  children: ReadonlyArray<TagSortItem>;
  isRecentlyViewed: boolean | undefined;
}

interface BuildTagSortItemsParams {
  visibleTags: ReadonlyArray<TagModel>;
  workspaceTags: ReadonlyArray<WorkspaceTagV2>;
  shouldAlphabetize: boolean;
  hasP1AutoRec: boolean;
  nestingLevelOffset?: number;
}

/**
 * Builds a TagSortItem list, including nested items as children.
 *
 * While it is possible to build the sort order without recursively building children,
 * this is a more robust method of building the sort order.
 */
export function buildTagSortItems({
  visibleTags,
  workspaceTags,
  shouldAlphabetize,
  hasP1AutoRec,
  nestingLevelOffset = 0,
}: BuildTagSortItemsParams): ReadonlyArray<TagSortItem> {
  const customSortOrderMap = shouldAlphabetize
    ? {}
    : computeWorkspaceSortOrderMap(workspaceTags, ({id}) => id);

  const timestampMap = computeWorkspaceTimestampMap(workspaceTags);

  // Group the tags by parent tag ID.
  const tagsByParent = groupBy(visibleTags, (tag) => computeParentTagId(tag, {}));

  // Map recursively starting at the root level.
  return _(tagsByParent[rootLevelParentId])
    .map((tag) =>
      buildTagSortItem({
        tag,
        tagsByParent,
        sortOrderMap: customSortOrderMap,
        nestingLevel: nestingLevelOffset,
        timestampMap,
        hasP1AutoRec,
      }),
    )
    .sortBy(
      (item) => customSortOrderMap[item.tag.id],
      (item) => isTeamNamespace(item.tag.namespace),
      (item) => computeTagAlphabetizedSortValue(item.tag),
    )
    .value();
}

interface BuildTagSortItemParams {
  tag: TagModel;
  tagsByParent: Dictionary<ReadonlyArray<TagModel>>;
  sortOrderMap: WorkspaceSortOrderMap;
  nestingLevel: number;
  timestampMap: WorkspaceTimestampMap;
  hasP1AutoRec: boolean;
}

/**
 * Builds a TagSortItem, recursively also building its children.
 * Based upon {@see buildVisualCustomView}.
 */
function buildTagSortItem({
  tag,
  tagsByParent,
  sortOrderMap,
  nestingLevel,
  timestampMap,
  hasP1AutoRec,
}: BuildTagSortItemParams): TagSortItem {
  const children = _(tagsByParent[tag.id])
    .map((childTag) =>
      buildTagSortItem({
        tag: childTag,
        tagsByParent,
        sortOrderMap,
        nestingLevel: nestingLevel + 1,
        timestampMap,
        hasP1AutoRec,
      }),
    )
    .sortBy(
      (item) => sortOrderMap[item.tag.id],
      (item) => computeTagAlphabetizedSortValue(item.tag),
    )
    .value();

  const isRecentlyViewed = computeVisualWorkspaceItemIsRecentlyViewed(tag.id, timestampMap, hasP1AutoRec);

  return {
    tag,
    nestingLevel,
    children,
    isRecentlyViewed,
  };
}

/**
 * Computes a flattened TagSortItem array by recursively adding the children of each item to the resulting array.
 * Based upon {@see computeFlatVisualCustomViews}.
 */
export function flattenTagSortItems(tagSortItems: ReadonlyArray<TagSortItem>): ReadonlyArray<TagSortItem> {
  return _(tagSortItems)
    .map((item) => [item, ...flattenTagSortItems(item.children)])
    .flatten()
    .value();
}

/**
 * Alphabetizes a list of tags. Does not take into account any tag hierarchy.
 */
export function alphabetizeFlatTags(tags: ReadonlyArray<TagModel>): ReadonlyArray<TagModel> {
  return _(tags)
    .sortBy((tag) => computeTagAlphabetizedSortValue(tag))
    .value();
}

export function computeTagAlphabetizedSortValue(tag: TagModel): string {
  return tag.name.toLowerCase();
}
